/**
 * Consentimento de cookies (LGPD) e gating de scripts de terceiros.
 *
 * Categorias:
 *  - necessarios  → sempre ativos (sessão/login, segurança). Não escolhíveis.
 *  - preferencias → guardam escolhas do usuário (tema claro/escuro etc.).
 *  - analiticos   → métricas anônimas (Google Analytics / gtag).
 *
 * O Google Analytics SÓ é injetado quando `analiticos` está aceito — ver
 * `carregarAnalytics`. Ao revogar, `descarregarAnalytics` desativa o gtag,
 * remove o script e limpa os cookies `_ga*`.
 *
 * A escolha é persistida em localStorage (`sigi.consentimento`) e espelhada
 * em um cookie leve (`sigi_consentimento`) para leitura rápida/observabilidade.
 */

export type CategoriaConsentimento = "necessarios" | "preferencias" | "analiticos"

export interface CategoriaInfo {
  id: CategoriaConsentimento
  titulo: string
  descricao: string
  /** Categoria essencial: sempre ativa e não desabilitável. */
  obrigatoria?: boolean
}

export const CATEGORIAS: CategoriaInfo[] = [
  {
    id: "necessarios",
    titulo: "Estritamente necessários",
    descricao:
      "Essenciais para autenticação, segurança e funcionamento do sistema. Não podem ser desativados.",
    obrigatoria: true,
  },
  {
    id: "preferencias",
    titulo: "Preferências",
    descricao:
      "Guardam suas escolhas no sistema — como tema claro/escuro e outras preferências de interface.",
  },
  {
    id: "analiticos",
    titulo: "Analíticos",
    descricao:
      "Métricas de uso anônimas (Google Analytics) usadas para entender o uso e melhorar o sistema.",
  },
]

export interface Consentimento {
  versao: number
  preferencias: boolean
  analiticos: boolean
  atualizadoEm: string
}

export const VERSAO_CONSENTIMENTO = 1
const CHAVE_CONSENTIMENTO = "sigi.consentimento"
const COOKIE_CONSENTIMENTO = "sigi_consentimento"
const UM_ANO = 60 * 60 * 24 * 365

/** Consentimento inicial sugerido: tudo habilitado (o usuário pode desmarcar). */
export function consentimentoPadrao(): Consentimento {
  return {
    versao: VERSAO_CONSENTIMENTO,
    preferencias: true,
    analiticos: true,
    atualizadoEm: new Date().toISOString(),
  }
}

/** Lê o consentimento salvo; `null` quando ainda não houve decisão. */
export function lerConsentimento(): Consentimento | null {
  try {
    const bruto = localStorage.getItem(CHAVE_CONSENTIMENTO)
    if (!bruto) return null
    const dados = JSON.parse(bruto) as Partial<Consentimento>
    if (dados.versao !== VERSAO_CONSENTIMENTO) return null
    return {
      versao: VERSAO_CONSENTIMENTO,
      preferencias: dados.preferencias === true,
      analiticos: dados.analiticos === true,
      atualizadoEm:
        typeof dados.atualizadoEm === "string" ? dados.atualizadoEm : new Date().toISOString(),
    }
  } catch {
    return null
  }
}

/** Persiste a decisão e devolve o registro normalizado. */
export function gravarConsentimento(
  escolha: Pick<Consentimento, "preferencias" | "analiticos">,
): Consentimento {
  const registro: Consentimento = {
    versao: VERSAO_CONSENTIMENTO,
    preferencias: escolha.preferencias === true,
    analiticos: escolha.analiticos === true,
    atualizadoEm: new Date().toISOString(),
  }
  try {
    localStorage.setItem(CHAVE_CONSENTIMENTO, JSON.stringify(registro))
    const valor = `${registro.analiticos ? "a" : ""}${registro.preferencias ? "p" : ""}` || "e"
    document.cookie = `${COOKIE_CONSENTIMENTO}=${valor}; path=/; max-age=${UM_ANO}; SameSite=Lax`
  } catch {
    /* armazenamento indisponível — degradação silenciosa */
  }
  return registro
}

/** `true` se a categoria pode executar (necessários sempre podem). */
export function temConsentimento(categoria: CategoriaConsentimento): boolean {
  if (categoria === "necessarios") return true
  const registro = lerConsentimento()
  return registro?.[categoria] === true
}

// ── Google Analytics (gtag.js) ───────────────────────────────────────────────

type WindowGA = Window & {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
  [chave: string]: unknown
}

const GA_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID
let analyticsCarregado = false

/** `true` quando um ID de medição foi configurado via VITE_GOOGLE_ANALYTICS_ID. */
export function analyticsConfigurado(): boolean {
  return Boolean(GA_ID)
}

/** Injeta o gtag.js — apenas com consentimento `analiticos`. Idempotente. */
export function carregarAnalytics(): void {
  if (!GA_ID || analyticsCarregado || typeof document === "undefined") return
  if (!temConsentimento("analiticos")) return
  analyticsCarregado = true

  const win = window as unknown as WindowGA
  win.dataLayer = win.dataLayer ?? []
  win.gtag = (...args: unknown[]) => {
    win.dataLayer!.push(args)
  }
  win.gtag("js", new Date())
  win.gtag("config", GA_ID, { anonymize_ip: true })
  win[`ga-disable-${GA_ID}`] = false

  const script = document.createElement("script")
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  script.dataset.sigiAnalytics = "true"
  document.head.appendChild(script)
}

/** Desativa o gtag, remove o script e limpa os cookies de medição. */
export function descarregarAnalytics(): void {
  if (!GA_ID || typeof document === "undefined") return
  const win = window as unknown as WindowGA
  win[`ga-disable-${GA_ID}`] = true
  document.querySelectorAll('script[data-sigi-analytics="true"]').forEach((el) => el.remove())
  limparCookiesGA("_ga")
  limparCookiesGA("_gid")
  limparCookiesGA("_gat")
  analyticsCarregado = false
}

/** Aplica o estado atual do consentimento aos scripts de terceiros. */
export function aplicarAnalytics(): void {
  if (temConsentimento("analiticos")) carregarAnalytics()
  else descarregarAnalytics()
}

function limparCookiesGA(prefixo: string): void {
  const nomes = document.cookie
    .split(";")
    .map((parte) => parte.split("=")[0]?.trim())
    .filter(Boolean)
  for (const nome of nomes) {
    if (nome === prefixo || nome.startsWith(`${prefixo}_`) || nome.startsWith(`${prefixo}.`)) {
      const base = `${nome}=; path=/; max-age=0; SameSite=Lax`
      document.cookie = base
      document.cookie = `${base}; domain=${location.hostname}`
    }
  }
}
